import { useCallback, useEffect, useReducer, useState } from 'react'
import { util } from '../../util'

const stateTypes = {
    INPUT_CHANGE: 'INPUT_CHANGE',
    CHECKBOX_INPUT_HANDLER: 'CHECKBOX_INPUT_HANDLER',
    UPDATE_STATE_FIELD: 'UPDATE_STATE_FIELD',
    UPDATE_FORM_STATE: 'UPDATE_FORM_STATE',
    ADD_FORM_INPUT: 'ADD_FORM_INPUT',
    REPLACE_FORM_INPUT: 'REPLACE_FORM_INPUT',
    DELETE_FORM_INPUT: 'DELETE_FORM_INPUT',
    SEARCH_INPUT_HANDLER: 'SEARCH_INPUT_HANDLER',
    DELETE_SEARCH_INPUT: 'DELETE_SEARCH_INPUT',
    RADIO_INPUT_HANDLER: 'RADIO_INPUT_HANDLER',
}

const reducer = (state, action) => {
    const { type, field, payload, key } = action
    const { deepShallowHelper, deepShallowArrayHelper, deepShallowFilterHelper } = util
    switch (type) {
        case stateTypes.INPUT_CHANGE:
            return deepShallowHelper(state, 'value', field, payload)

        case stateTypes.CHECKBOX_INPUT_HANDLER:
            return deepShallowHelper(state, 'list', field, payload)

        case stateTypes.UPDATE_STATE_FIELD:
            return deepShallowHelper(state, key, field, payload)

        case stateTypes.ADD_FORM_INPUT:
            return { ...state, [field]: payload }

        case stateTypes.REPLACE_FORM_INPUT:
            delete state[field]
            return { ...state, [key]: payload }

        case stateTypes.DELETE_FORM_INPUT: {
            const updatedState = { ...state }
            delete updatedState[key]
            return updatedState
        }

        case stateTypes.UPDATE_FORM_STATE: {
            return { ...state, ...payload }
        }

        case stateTypes.SEARCH_INPUT_HANDLER:
            return deepShallowArrayHelper(state, 'list', field, payload)

        case stateTypes.DELETE_SEARCH_INPUT:
            return deepShallowFilterHelper(state, 'list', field, payload)

        default:
            return state
    }
}

export const useForm = initialState => {
    const [state, dispatch] = useReducer(reducer, initialState)
    const [showValidationError, setValidationError] = useState(false)
    const [saveDisabled, setSaveDisabled] = useState(false)

    useEffect(() => {
        if (showValidationError) {
            onFormValidation(error => !error && setSaveDisabled(false))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state])

    const onInputChangeHandler = useCallback(e => {
        const { name, value } = e.target
        dispatch({
            type: stateTypes.INPUT_CHANGE,
            payload: value,
            field: name,
        })
    }, [])

    const onSelectInputChange = useCallback((value, name) => {
        dispatch({
            type: stateTypes.INPUT_CHANGE,
            payload: value,
            field: name,
        })
    }, [])

    const onCheckInputHandler = useCallback(e => {
        const { name, checked } = e.target
        dispatch({
            type: stateTypes.CHECKBOX_INPUT_HANDLER,
            field: name,
            payload: checked,
        })
    }, [])

    const updateStateField = useCallback((value, field) => {
        dispatch({
            type: stateTypes.INPUT_CHANGE,
            payload: value,
            field,
        })
    }, [])

    const updateFormField = useCallback((value, name, key) => {
        dispatch({
            type: stateTypes.UPDATE_STATE_FIELD,
            payload: value,
            field: name,
            key,
        })
    }, [])

    const addFormInput = useCallback((name, payload) => {
        dispatch({
            type: stateTypes.ADD_FORM_INPUT,
            field: name,
            payload,
        })
    }, [])

    const replaceFormField = useCallback((currKey, updatedKey, payload) => {
        dispatch({
            type: stateTypes.REPLACE_FORM_INPUT,
            field: currKey,
            key: updatedKey,
            payload,
        })
    }, [])

    const deleteFormInput = useCallback(key => {
        dispatch({
            type: stateTypes.DELETE_FORM_INPUT,
            key,
        })
    }, [])

    const updateFormState = useCallback(payload => {
        dispatch({
            type: stateTypes.UPDATE_FORM_STATE,
            payload,
        })
    }, [])

    const onAddSearchInput = useCallback((value, name) => {
        dispatch({
            type: stateTypes.SEARCH_INPUT_HANDLER,
            payload: value,
            field: name,
        })
    }, [])

    const onDeleteSearchInput = useCallback((value, name) => {
        dispatch({
            type: stateTypes.DELETE_SEARCH_INPUT,
            payload: value,
            field: name,
        })
    }, [])

    const onFormValidation = done => {
        let onSaveError = false
        for (const key in state) {
            if (!state.hasOwnProperty(key)) {
                return
            }
            if (util.isObject(state[key])) {
                if (state[key].isRequired) {
                    if (!!state[key].validate && !state[key].validate(state)) {
                        console.log('FORM FAILING AT >>>', state[key])
                        onSaveError = true
                        break
                    }
                }
            }
        }
        setValidationError(onSaveError)
        setSaveDisabled(onSaveError)
        !!done && done(onSaveError)
    }

    const onRadioChangeHandler = useCallback(e => {
        const { id, name } = e.target
        dispatch({
            type: stateTypes.INPUT_CHANGE,
            payload: id,
            field: name,
        })
    }, [])

    const displayFormError = key => {
        if (!key || !showValidationError || !state[key].validate) {
            return ''
        }
        return util.formRequiredError(
            !state[key].validate(state) && showValidationError,
            !!state[key].errorMsg && state[key].errorMsg
        )
    }

    return {
        state,
        displayFormError,
        saveDisabled,
        onFormValidation,
        addFormInput,
        deleteFormInput,
        replaceFormField,
        updateFormField,
        onInputChangeHandler,
        onSelectInputChange,
        onCheckInputHandler,
        updateStateField,
        updateFormState,
        onAddSearchInput,
        onDeleteSearchInput,
        onRadioChangeHandler,
    }
}
